import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'

import { Drawer, Icon, Layout, LogoLoader } from '@loadsmart/miranda-react'

import { RequestStatus } from 'core/domain/Request'

import { type Carrier, type OpenDockWarehouseDetails } from '../../domain/Shipment'
import { useGetOpenDockCarrierId } from '../hooks/useGetOpenDockCarrierId'
import { useGetOpenDockWarehouseDetails } from '../hooks/useGetOpenDockWarehouseDetails'
import { AlreadyScheduled } from './AlreadyScheduled'
import { Schedule } from './Schedule'
import { type ScheduleFormValues } from './Schedule'
import { SendInfo } from './SendInfo'
import { WarehouseInstructions } from './WarehouseInstructions'

const STEPS = {
  INSTRUCTIONS: 'instructions',
  SCHEDULE: 'schedule',
  SEND_INFO: 'send_info',
  ALREADY_SCHEDULED: 'already_scheduled',
}

function StepContent({
  step,
  isSignedUp,
  warehouseDetails,
  shipmentCarrier,
  shipmentUUID,
  stopIndex,
  setStep,
  fields,
  setStaticFields,
  setDynamicFields,
  facilityTimezone,
  openDockCarrierId,
  onConfirm,
  onClose,
  previousStep,
}: {
  step: string
  isSignedUp: boolean
  warehouseDetails: OpenDockWarehouseDetails
  shipmentCarrier: Carrier
  shipmentUUID: string
  stopIndex: number
  setStep: (step: string) => void
  fields: {
    staticFields: ScheduleFormValues
    dynamicFields: Record<string, number | string | boolean | null>
  }
  setStaticFields: (staticFields: ScheduleFormValues) => void
  setDynamicFields: (dynamicFields: Record<string, number | string | boolean | null>) => void
  facilityTimezone?: string
  openDockCarrierId: string | undefined
  onConfirm: () => void
  onClose: () => void
  previousStep: string | undefined
}) {
  switch (step) {
    case STEPS.INSTRUCTIONS:
      return (
        <WarehouseInstructions
          isSignedUp={isSignedUp}
          warehouseDetails={warehouseDetails}
          shipmentCarrier={shipmentCarrier}
          setStep={setStep}
        />
      )
    case STEPS.SCHEDULE:
      return (
        <Schedule
          isSignedUp={isSignedUp}
          warehouseDetails={warehouseDetails}
          shipmentCarrier={shipmentCarrier}
          shipmentUUID={shipmentUUID}
          stopIndex={stopIndex}
          setStep={setStep}
          fields={fields}
          setStaticFields={setStaticFields}
          facilityTimezone={facilityTimezone}
        />
      )
    case STEPS.SEND_INFO:
      return (
        <SendInfo
          shipmentUUID={shipmentUUID}
          stopIndex={stopIndex}
          setStep={setStep}
          fields={fields}
          setDynamicFields={setDynamicFields}
          facilityTimezone={facilityTimezone}
          warehouseDetails={warehouseDetails}
          openDockCarrierId={openDockCarrierId}
          onConfirm={onConfirm}
          onClose={onClose}
        />
      )
    case STEPS.ALREADY_SCHEDULED:
      return (
        <AlreadyScheduled
          setStep={setStep}
          previousStep={previousStep}
          shipmentUUID={shipmentUUID}
          stopIndex={stopIndex}
          onConfirm={onConfirm}
          onClose={onClose}
        />
      )
    default:
      return null
  }
}

type AppointmentFields = {
  staticFields: ScheduleFormValues
  dynamicFields: Record<string, number | string | boolean | null>
}

function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T>()

  useEffect(() => {
    ref.current = value
  }, [value])

  return ref.current
}

export function OpenDockAppointmentDrawer({
  isOpen,
  onClose,
  shipmentUUID,
  shipmentCarrier,
  stopIndex,
  facilityTimezone,
  onConfirm,
}: {
  isOpen: boolean
  onClose: () => void
  shipmentUUID: string
  shipmentCarrier: Carrier
  stopIndex: number
  facilityTimezone?: string
  onConfirm: () => void
}) {
  const [step, setStep] = useState<string>(STEPS.INSTRUCTIONS)
  const previousStep = usePrevious(step)

  const [fields, setFields] = useState<AppointmentFields>({
    staticFields: {
      loadType: null,
      dock: null,
      appointmentDate: null,
      appointmentTime: null,
    },
    dynamicFields: {},
  })

  function setStaticFields(staticFields: ScheduleFormValues) {
    setFields(prev => ({ ...prev, staticFields }))
  }

  function setDynamicFields(dynamicFields: Record<string, number | string | boolean | null>) {
    setFields(prev => ({ ...prev, dynamicFields }))
  }

  const { openDockCarrierId, fetchOpenDockCarrierId, status } = useGetOpenDockCarrierId(
    shipmentUUID,
    false
  )

  const {
    warehouseDetails,
    fetchOpenDockWarehouseDetails,
    status: warehouseStatus,
  } = useGetOpenDockWarehouseDetails(shipmentUUID, stopIndex, false)

  useEffect(() => {
    if (status === RequestStatus.Initial && isOpen) {
      fetchOpenDockCarrierId(shipmentUUID)
    }
  }, [fetchOpenDockCarrierId, isOpen, shipmentUUID, status])

  useEffect(() => {
    if (warehouseStatus === RequestStatus.Initial && isOpen) {
      fetchOpenDockWarehouseDetails(shipmentUUID, stopIndex)
    }
  }, [fetchOpenDockWarehouseDetails, isOpen, shipmentUUID, stopIndex, warehouseStatus])

  useEffect(() => {
    if (warehouseStatus === RequestStatus.Error) {
      onClose()
      toast.error('Failed to load warehouse details. Please try again later.')
    }
  }, [onClose, warehouseStatus])

  if (warehouseStatus === RequestStatus.Pending) {
    return (
      <Drawer open={isOpen} onClose={onClose}>
        <Drawer.Header>
          <Layout.Group align="center" gap="spacing-1">
            <Icon name="calendar" size="24px" />
            Schedule
          </Layout.Group>
          <Drawer.Close />
        </Drawer.Header>
        <Drawer.Body>
          <Layout.Stack align="center" paddingT="spacing-16">
            <LogoLoader />
          </Layout.Stack>
        </Drawer.Body>
        <Drawer.Actions>
          <Drawer.ActionTertiary disabled>Already scheduled in Opendock</Drawer.ActionTertiary>
          <Drawer.ActionPrimary disabled>Start</Drawer.ActionPrimary>
        </Drawer.Actions>
      </Drawer>
    )
  }

  if (warehouseStatus === RequestStatus.Done && warehouseDetails) {
    return (
      <Drawer open={isOpen} onClose={onClose}>
        <Drawer.Header>
          <Layout.Group align="center" gap="spacing-1">
            <Icon name="calendar" size="24px" />
            Schedule
          </Layout.Group>
          <Drawer.Close />
        </Drawer.Header>
        <StepContent
          step={step}
          isSignedUp={Boolean(openDockCarrierId)}
          openDockCarrierId={openDockCarrierId}
          warehouseDetails={warehouseDetails}
          shipmentCarrier={shipmentCarrier}
          shipmentUUID={shipmentUUID}
          stopIndex={stopIndex}
          setStep={setStep}
          previousStep={previousStep}
          fields={fields}
          setStaticFields={setStaticFields}
          setDynamicFields={setDynamicFields}
          facilityTimezone={facilityTimezone}
          onConfirm={onConfirm}
          onClose={onClose}
        />
      </Drawer>
    )
  }

  return null
}
